import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, DollarSign, Mail, Activity, Settings, CheckSquare, BarChart3, UploadCloud, CreditCard, Brain, Menu, X, ChevronDown, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const Sidebar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useI18n();

  const navItems = [
    { name: t('nav.dashboard', 'Dashboard'), icon: LayoutDashboard, path: '/dashboard' },
    { name: t('nav.aiInsights', 'AI Insights'), icon: Brain, path: '/ai-insights' },
    { name: t('nav.contacts', 'Contacts'), icon: Users, path: '/contacts' },
    { name: t('nav.companies', 'Companies'), icon: Building2, path: '/companies' },
    { name: t('nav.deals', 'Deals'), icon: DollarSign, path: '/deals' },
    { name: t('nav.marketing', 'Marketing'), icon: Mail, path: '/marketing' },
    { name: t('nav.tasks', 'Tasks'), icon: CheckSquare, path: '/tasks' },
    { name: t('nav.calendar', 'Calendar'), icon: CalendarIcon, path: '/calendar' },
    { name: t('nav.reports', 'Reports'), icon: BarChart3, path: '/reports' },
    { name: t('nav.activities', 'Activities'), icon: Activity, path: '/activities' },
  ];

  const settingsChildren = [
    { name: t('nav.billing', 'Billing'), icon: CreditCard, path: '/billing' },
    { name: t('nav.automations', 'Automations'), icon: Activity, path: '/automations' },
    { name: t('nav.importExport', 'Import/Export'), icon: UploadCloud, path: '/import-export' },
  ];

  const settingsActive = useMemo(() => {
    return ['/settings', '/billing', '/automations', '/import-export'].some(p => location.pathname.startsWith(p));
  }, [location.pathname]);

  const [isSettingsOpen, setIsSettingsOpen] = useState(settingsActive);

  const bottomNavItems = [];

  return (
    <>
      {/* Mobile Menu Button - Only visible on mobile */}
      <button 
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-bissa-blue text-white rounded-lg shadow-lg"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`h-screen w-64 bg-bissa-blue text-white fixed left-0 top-0 flex flex-col z-40 transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        <div className="p-6 border-b border-bissa-gold/20">
          <h1 className="text-2xl font-bold text-bissa-gold">Bissa</h1>
        </div>
      
      <nav className="flex-1 p-4 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                isActive
                  ? 'bg-bissa-gold text-bissa-blue font-semibold'
                  : 'text-gray-300 hover:bg-bissa-blue-600 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.name}</span>
            </Link>
          );
        })}

        {/* Settings Group */}
        <button
          type="button"
          onClick={() => setIsSettingsOpen((v) => !v)}
          className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
            settingsActive ? 'bg-bissa-gold text-bissa-blue font-semibold' : 'text-gray-300 hover:bg-bissa-blue-600 hover:text-white'
          }`}
        >
          <span className="flex items-center gap-3">
            <Settings className="w-5 h-5" />
            <span className="font-medium">{t('nav.settings', 'Settings')}</span>
          </span>
          {isSettingsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {isSettingsOpen && (
          <div className="mt-1 ml-2 space-y-1">
            {settingsChildren.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? 'bg-bissa-gold text-bissa-blue font-semibold'
                      : 'text-gray-300 hover:bg-bissa-blue-600 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {bottomNavItems.length > 0 && (
        <div className="p-4 border-t border-bissa-gold/20">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-bissa-gold text-bissa-blue font-semibold'
                    : 'text-gray-300 hover:bg-bissa-blue-600 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      )}
      </div>
    </>
  );
};

export default Sidebar;